/**
 * Ad Network Configuration
 * 
 * Central configuration for all display advertising on the site.
 * 
 * AD CATEGORIES BY DIMENSIONS:
 * 
 * HORIZONTAL (Wide Banner) - 468×60 pixels (7.8:1 aspect ratio)
 * ├─ Best for: Product pages, page headers/footers, between content sections
 * ├─ Total: 5 horizontal banners (verified direct image URLs)
 * └─ Networks: Levot, Restaurant.com (2), Sharp (2)
 * 
 * SQUARE/RECTANGULAR (Card) - 300×250 pixels (1.2:1 aspect ratio)
 * ├─ Best for: Collection cards, blog cards, sidebar, mixed with products
 * ├─ Total: 9 square banners
 * └─ Networks: Wayfair, LG, AJ Madison, FridgeFilters, Fresh Meal Plan, Sharp, Amana, Maytag
 * 
 * PLACEMENT STRATEGY:
 * - Product pages → 3 horizontal banners (top & bottom)
 * - Collection pages → Square cards mixed with products
 * - Blog pages → Square cards mixed with products & sidebar
 * 
 * Add new ad networks, sizes, and tracking URLs here.
 */

export interface AdConfig {
  network: string
  advertiser: string
  trackingUrl: string
  imageUrl?: string
  imageAlt?: string
  size: '300x250' | '728x90' | '468x60' | '216x36' | '125x125' | '320x50'
  payoutRate?: string
  needsWhiteBg?: boolean
}

// Network tracking URLs
const WAYFAIR_BASE_URL = 'https://track.flexlinkspro.com/g.ashx?foid=2.5206455.13620948&trid=1479208.203001&foc=16&fot=9999&fos=6'
const LG_BASE_URL = 'https://track.flexlinkspro.com/g.ashx?foid=2.5491843.15000973&trid=1479208.225794&foc=16&fot=9999&fos=6'
const LEVOT_BASE_URL = 'https://track.flexlinkspro.com/g.ashx?foid=156074.41708.3280677&trid=1479208.235505&foc=16&fot=9999&fos=6'
const SHIPT_BASE_URL = 'https://track.flexlinkspro.com/g.ashx?foid=156074.16249.2851942&trid=1479208.198916&foc=16&fot=9999&fos=6'
const RESTAURANT_BASE_URL = 'https://track.flexlinkspro.com/g.ashx?foid=156074.21234.1809377&trid=1479208.157348&foc=16&fot=9999&fos=6'
const SHARP_BASE_URL = 'https://track.flexlinkspro.com/g.ashx?foid=156074.9885.1463793&trid=1479208.209195&foc=16&fot=9999&fos=6'

export const AD_CONFIGS = {
  // Wayfair (9.6% payout)
  wayfair: {
    '300x250': {
      network: 'flexoffers',
      advertiser: 'wayfair',
      trackingUrl: WAYFAIR_BASE_URL,
      imageUrl: 'https://www.tqlkg.com/image-1760738-13620948-1619448565000',
      imageAlt: 'Shop Wayfair - Home Goods & Appliances',
      size: '300x250' as const,
      payoutRate: '9.6%'
    }
    // Note: Wayfair 468x60 removed - URL redirects (302), not a direct image
  },
  
  // LG (10% payout)
  lg: {
    '300x250': {
      network: 'flexoffers',
      advertiser: 'lg',
      trackingUrl: 'https://track.flexlinkspro.com/g.ashx?foid=2.5491843.15000808&trid=1479208.225794&foc=16&fot=9999&fos=6',
      imageUrl: 'https://www.awltovhc.com/image-1760738-15000808-1761753252000',
      imageAlt: 'LG Appliances - Premium Kitchen Solutions',
      size: '300x250' as const,
      payoutRate: '10%'
    }
    // Note: LG variant 1 (15001002) removed - image is 936×120 (horizontal), not 300×250
    // Note: LG 468×60 tracking redirect removed - doesn't serve direct image
  },
  
  // Levot (4-8% payout)
  levot: {
    '468x60': {
      network: 'flexoffers',
      advertiser: 'levot',
      trackingUrl: LEVOT_BASE_URL,
      imageUrl: 'https://a.impactradius-go.com/display-ad/41708-3280677',
      imageAlt: 'Levot',
      size: '468x60' as const,
      payoutRate: '4-8%'
    }
  },
  
  
  // AJ Madison (0.8% payout)
  ajmadison: {
    '300x250': {
      network: 'flexoffers',
      advertiser: 'ajmadison',
      trackingUrl: 'https://track.flexlinkspro.com/g.ashx?foid=2.1526385.13065209&trid=1479208.159356&foc=16&fot=9999&fos=6',
      imageUrl: 'https://www.lduhtrp.net/image-1760738-13065209-1707423949000',
      imageAlt: 'No Interest Financing on Major Appliances at AJMadison.com!',
      size: '300x250' as const,
      payoutRate: '0.8%'
    }
  },
  
  
  // FridgeFilters (2.4% payout)
  fridgefilters: {
    '300x250': {
      network: 'flexoffers',
      advertiser: 'fridgefilters',
      trackingUrl: 'https://track.flexlinkspro.com/g.ashx?foid=156085.110000612.101135901&trid=1479208.206658&foc=16&fot=9999&fos=6',
      imageUrl: 'https://console.partnerize.com/upload/5cb040157623bff_sun5_300x250.png',
      imageAlt: '5% Off And Free Shipping with Code Sun5',
      size: '300x250' as const,
      payoutRate: '2.4%'
    }
  },
  
  // Fresh Meal Plan (8% payout)
  freshmealplan: {
    '300x250': {
      network: 'flexoffers',
      advertiser: 'freshmealplan',
      trackingUrl: 'https://track.flexlinkspro.com/g.ashx?foid=156052.9734.448369&trid=1479208.222278&foc=16&fot=9999&fos=6',
      imageUrl: 'https://www.gopjn.com/b/4-448369-47736-232987',
      imageAlt: 'Fresh Meal Plan',
      size: '300x250' as const,
      payoutRate: '8%'
    }
  },
  
  // SHARP Square Banner (16% new customer, 4% others)
  sharpSquare: {
    '300x250': {
      network: 'flexoffers',
      advertiser: 'sharp',
      trackingUrl: 'https://track.flexlinkspro.com/g.ashx?foid=156074.9885.1454687&trid=1479208.209195&foc=16&fot=9999&fos=6',
      imageUrl: 'https://a.impactradius-go.com/display-ad/9885-1454687',
      imageAlt: 'Sharp Home Appliances',
      size: '300x250' as const,
      payoutRate: '16% first, 4%+ ongoing'
    }
  },
  
  // Amana (2.4% payout) - Logo needs white background
  amana: {
    '300x250': {
      network: 'flexoffers',
      advertiser: 'amana',
      trackingUrl: 'https://track.flexlinkspro.com/g.ashx?foid=1.53713.2000000003&trid=1479208.234990&foc=16&fot=9999&fos=6',
      imageUrl: 'https://ad.linksynergy.com/fs-bin/show?id=PPkX79/c*b0&bids=1824531.3&type=4',
      imageAlt: 'Amana Appliances',
      size: '300x250' as const,
      payoutRate: '2.4%',
      needsWhiteBg: true
    }
  },
  
  // Maytag (4% payout) - Logo needs white background
  maytag: {
    '300x250': {
      network: 'flexoffers',
      advertiser: 'maytag',
      trackingUrl: 'https://track.flexlinkspro.com/g.ashx?foid=1.53708.2000000003&trid=1479208.217612&foc=16&fot=9999&fos=6',
      imageUrl: 'https://ad.linksynergy.com/fs-bin/show?id=PPkX79/c*b0&bids=1825125.3&type=4',
      imageAlt: 'Maytag Appliances',
      size: '300x250' as const,
      payoutRate: '4%',
      needsWhiteBg: true
    }
  },
  
  // SHIPT (8% for started membership)
  // Note: Shipt removed - tracking URL doesn't serve an image directly
  
  // Restaurant.com (28% payout)
  restaurant: {
    '468x60-1': {
      network: 'flexoffers',
      advertiser: 'restaurant',
      trackingUrl: RESTAURANT_BASE_URL,
      imageUrl: 'https://a.impactradius-go.com/display-ad/21234-1809377',
      imageAlt: 'Restaurant.com',
      size: '468x60' as const,
      payoutRate: '28%'
    },
    '468x60-2': {
      network: 'flexoffers',
      advertiser: 'restaurant',
      trackingUrl: RESTAURANT_BASE_URL,
      imageUrl: 'https://a.impactradius-go.com/display-ad/21234-1809379',
      imageAlt: 'Restaurant.com',
      size: '468x60' as const,
      payoutRate: '28%'
    }
  },
  
  // SHARP (16% first purchase, 4%+ ongoing)
  sharp: {
    '468x60-1': {
      network: 'flexoffers',
      advertiser: 'sharp',
      trackingUrl: SHARP_BASE_URL,
      imageUrl: 'https://a.impactradius-go.com/display-ad/9885-1463793',
      imageAlt: 'Sharp Home Appliances',
      size: '468x60' as const,
      payoutRate: '16% first, 4%+ ongoing'
    },
    '468x60-2': {
      network: 'flexoffers',
      advertiser: 'sharp',
      trackingUrl: SHARP_BASE_URL,
      imageUrl: 'https://a.impactradius-go.com/display-ad/9885-1463799',
      imageAlt: 'Sharp Home Appliances',
      size: '468x60' as const,
      payoutRate: '16% first, 4%+ ongoing'
    }
  },
  
  // Whirlpool - REMOVED (Rakuten ad server returns 302 redirect, requires cookies/session)
  // Maytag - REMOVED (Rakuten ad server returns 302 redirect, requires cookies/session)
}

/**
 * True Nature Meats — bulk-meat delivery (flash-frozen, chef-crafted).
 *
 * A natural fit for chest/upright/deep-freezer owners: a full freezer is what
 * bulk-meat delivery is for. FlexOffers advertiser id 247377, account APPROVED
 * (verified live via the FlexOffers Public API 07/20/2026). Commission: 30% per
 * sale. Tracking URL is the FlexOffers promotional link (g.ashx → 302 to
 * truenaturemeats.com with refid + utm attribution), matching every other unit
 * in AD_CONFIGS above.
 *
 * Deliberately kept OUT of AD_CONFIGS / the sitewide getSquareAds() rotation:
 * this is a targeted, on-theme placement that renders ONLY on standalone-freezer
 * surfaces (see isFreezerSurface) and freezer blog articles — never on wine
 * coolers, mini fridges, or refrigerator collections where it would be off-theme.
 */
export const TRUE_NATURE_MEATS_AD: AdConfig = {
  network: 'flexoffers',
  advertiser: 'truenaturemeats',
  trackingUrl: 'https://track.flexlinkspro.com/g.ashx?foid=24.247377.6670875&trid=1479208.247377',
  imageUrl: 'https://content.flexlinks.com/sharedimages/programsquarelogo/247377.png',
  imageAlt: 'True Nature Meats — grass-fed, flash-frozen meat delivered',
  size: '300x250',
  payoutRate: '30%',
  needsWhiteBg: true,
}

// Standalone-freezer collection surfaces where the True Nature Meats bulk-meat
// promo is on-theme. Matches chest/upright/deep/compact/garage/built-in/
// undercounter freezer collections (incl. use-case variants like
// "upright-freezers-for-garage-storage"). Excludes "freezerless" and
// top-/bottom-freezer REFRIGERATORS — those are fridges, not bulk-meat storage.
export function isFreezerSurface(slug: string | null | undefined): boolean {
  if (!slug) return false
  const s = slug.toLowerCase()
  if (!s.includes('freezer')) return false
  if (s.includes('freezerless')) return false
  if (s.includes('top-freezer') || s.includes('bottom-freezer')) return false
  return true
}

/**
 * Get ad configuration by network and size
 */
export function getAdConfig(network: keyof typeof AD_CONFIGS, size: string): AdConfig | null {
  const networkConfig = AD_CONFIGS[network]
  if (!networkConfig) return null
  
  return networkConfig[size as keyof typeof networkConfig] || null
}

/**
 * Get all ads for a specific network
 */
export function getNetworkAds(network: keyof typeof AD_CONFIGS): AdConfig[] {
  const networkConfig = AD_CONFIGS[network]
  if (!networkConfig) return []
  
  return Object.values(networkConfig)
}

/**
 * Ad Categories by Layout Type
 */
export const AD_CATEGORIES = {
  // WIDE/HORIZONTAL banners - for wide rectangular spaces
  HORIZONTAL: '468x60' as const,
  
  // SQUARE/RECTANGULAR banners - for card-like spaces
  SQUARE: '300x250' as const
}

/**
 * Get all ads by size for rotation
 */
function getAdsBySize(size: string): AdConfig[] {
  const ads: AdConfig[] = []
  Object.values(AD_CONFIGS).forEach(network => {
    Object.entries(network).forEach(([key, ad]) => {
      if (ad.size === size) {
        ads.push(ad)
      }
    })
  })
  return ads
}

/**
 * Get all HORIZONTAL (wide) ads - for banner slots
 */
export function getHorizontalAds(): AdConfig[] {
  return getAdsBySize(AD_CATEGORIES.HORIZONTAL)
}

/**
 * Get all SQUARE/RECTANGULAR ads - for card slots
 */
export function getSquareAds(): AdConfig[] {
  return getAdsBySize(AD_CATEGORIES.SQUARE)
}

/**
 * Get a random ad by size
 */
export function getRandomAd(size: '300x250' | '468x60'): AdConfig {
  const ads = getAdsBySize(size)
  return ads[Math.floor(Math.random() * ads.length)]
}

/**
 * Get a random HORIZONTAL ad (for wide banner slots)
 */
export function getRandomHorizontalAd(): AdConfig {
  const ads = getHorizontalAds()
  return ads[Math.floor(Math.random() * ads.length)]
}

/**
 * Get a random SQUARE ad (for card slots)
 */
export function getRandomSquareAd(): AdConfig {
  const ads = getSquareAds()
  return ads[Math.floor(Math.random() * ads.length)]
}

/**
 * Get multiple random ads (without repeats in same batch)
 */
export function getRandomAds(size: '300x250' | '468x60', count: number): AdConfig[] {
  const ads = getAdsBySize(size)
  const shuffled = [...ads].sort(() => Math.random() - 0.5)
  return shuffled.slice(0, Math.min(count, shuffled.length))
}

/**
 * Get multiple random HORIZONTAL ads (for wide banner slots)
 * Ensures no duplicate advertisers appear consecutively
 */
export function getRandomHorizontalAds(count: number): AdConfig[] {
  const ads = getHorizontalAds()
  if (ads.length === 0) {
    console.warn('No horizontal ads available!')
    return []
  }
  
  // Shuffle the ads
  const shuffled = [...ads].sort(() => Math.random() - 0.5)
  const selected: AdConfig[] = []
  
  // Select ads ensuring no consecutive duplicates
  for (let i = 0; i < shuffled.length && selected.length < count; i++) {
    const ad = shuffled[i]
    const lastAd = selected[selected.length - 1]
    
    // Add if first ad or different advertiser than previous
    if (!lastAd || lastAd.advertiser !== ad.advertiser) {
      selected.push(ad)
    } else if (selected.length < count && i < shuffled.length - 1) {
      // Try to find a different advertiser from remaining ads
      const remaining = shuffled.slice(i + 1)
      const different = remaining.find(a => a.advertiser !== lastAd.advertiser)
      if (different) {
        selected.push(different)
        // Remove it from future consideration
        shuffled.splice(shuffled.indexOf(different), 1)
      } else {
        // No choice, add the duplicate
        selected.push(ad)
      }
    }
  }
  
  // If we still need more ads, add any remaining (allowing duplicates)
  if (selected.length < count) {
    const remaining = shuffled.filter(ad => !selected.includes(ad))
    selected.push(...remaining.slice(0, count - selected.length))
  }
  
  // Debug logging
  if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
    console.log(`[Ad Debug] Selected ${selected.length} horizontal ads:`, 
      selected.map(ad => `${ad.advertiser} (${ad.size})`))
  }
  
  return selected
}

/**
 * Get multiple random SQUARE ads (for card slots)
 * Ensures no duplicate advertisers appear consecutively
 * 
 * @param count - Number of ads to return
 * @param seed - Optional seed for deterministic selection (prevents hydration mismatch)
 */
export function getRandomSquareAds(count: number, seed?: string): AdConfig[] {
  const ads = getSquareAds()
  if (ads.length === 0) {
    console.warn('No square ads available!')
    return []
  }
  
  // If seed provided, use deterministic selection (for SSR/hydration compatibility)
  if (seed) {
    return getDeterministicAds(ads, count, seed)
  }
  
  // Shuffle the ads (random - only use client-side after mount)
  const shuffled = [...ads].sort(() => Math.random() - 0.5)
  const selected: AdConfig[] = []
  
  // Select ads ensuring no consecutive duplicates
  for (let i = 0; i < shuffled.length && selected.length < count; i++) {
    const ad = shuffled[i]
    const lastAd = selected[selected.length - 1]
    
    // Add if first ad or different advertiser than previous
    if (!lastAd || lastAd.advertiser !== ad.advertiser) {
      selected.push(ad)
    } else if (selected.length < count && i < shuffled.length - 1) {
      // Try to find a different advertiser from remaining ads
      const remaining = shuffled.slice(i + 1)
      const different = remaining.find(a => a.advertiser !== lastAd.advertiser)
      if (different) {
        selected.push(different)
        // Remove it from future consideration
        shuffled.splice(shuffled.indexOf(different), 1)
      } else {
        // No choice, add the duplicate
        selected.push(ad)
      }
    }
  }
  
  // If we still need more ads, add any remaining (allowing duplicates)
  if (selected.length < count) {
    const remaining = shuffled.filter(ad => !selected.includes(ad))
    selected.push(...remaining.slice(0, count - selected.length))
  }
  
  return selected
}

/**
 * Deterministic ad selection based on a seed string
 * Ensures same ads are selected on server and client for hydration
 */
function getDeterministicAds(ads: AdConfig[], count: number, seed: string): AdConfig[] {
  // Simple hash function to convert seed to number
  let hash = 0
  for (let i = 0; i < seed.length; i++) {
    const char = seed.charCodeAt(i)
    hash = ((hash << 5) - hash) + char
    hash = hash & hash // Convert to 32bit integer
  }
  hash = Math.abs(hash)
  
  // Use hash to pick starting index
  const startIndex = hash % ads.length
  const selected: AdConfig[] = []
  
  // Select ads in deterministic order starting from hash-based index
  for (let i = 0; i < count && i < ads.length; i++) {
    const index = (startIndex + i) % ads.length
    selected.push(ads[index])
  }
  
  return selected
}

/**
 * Get advertiser display name
 */
export function getAdvertiserDisplayName(advertiser: string): string {
  const names: Record<string, string> = {
    'wayfair': 'Wayfair',
    'lg': 'LG Electronics',
    'levot': 'Levot',
    'restaurant': 'Restaurant.com',
    'sharp': 'Sharp Home Appliances',
    'ajmadison': 'AJ Madison',
    'fridgefilters': 'FridgeFilters.com',
    'freshmealplan': 'Fresh Meal Plan',
    'amana': 'Amana',
    'maytag': 'Maytag',
    'truenaturemeats': 'True Nature Meats'
  }
  return names[advertiser] || advertiser
}

/**
 * Get advertiser description
 */
export function getAdvertiserDescription(advertiser: string): string {
  const descriptions: Record<string, string> = {
    'wayfair': 'Home goods and appliances with free shipping over $49',
    'lg': 'Premium kitchen appliances - Save 30-55% on select models',
    'levot': 'Quality home appliances and kitchen equipment',
    'restaurant': 'Save up to 50% at thousands of restaurants nationwide',
    'sharp': 'Innovative home appliances and electronics',
    'ajmadison': 'Major appliances with no interest financing available',
    'fridgefilters': 'Refrigerator filters with 5% off and free shipping',
    'freshmealplan': 'Fresh meal plans delivered to your door',
    'amana': 'Reliable home appliances built to last',
    'maytag': 'Dependable appliances for your home',
    'truenaturemeats': 'Grass-fed, chef-crafted meat — flash-frozen and delivered. Stock your freezer.'
  }
  return descriptions[advertiser] || 'Premium products and services'
}

/**
 * Pre-configured ad placements for common locations
 * Optimized by banner dimensions and placement type
 */
export const AD_PLACEMENTS = {
  // HORIZONTAL (Wide) Banner Slots - 468x60 ONLY
  // Best for: Product pages, top/bottom of pages, between sections
  get productPageTopBanners() {
    return getRandomHorizontalAds(3) // 3 wide banners side-by-side
  },
  
  get productPageBottomBanners() {
    return getRandomHorizontalAds(3) // 3 wide banners side-by-side
  },
  
  // SQUARE/RECTANGULAR (Card) Slots - 300x250 ONLY
  // Best for: Collection cards, blog cards, sidebar, mixed with products
  get collectionCards() {
    return getRandomSquareAds(2) // Cards mixed with products
  },
  
  get blogCards() {
    return getRandomSquareAds(4) // Blog page card grid
  },
  
  get sidebarCard() {
    return getRandomSquareAd() // Single sidebar ad
  },
  
  // Legacy getters (for backward compatibility)
  get leaderboardBanners() {
    return getRandomHorizontalAds(3)
  },
  
  get sidebar() {
    return getRandomSquareAd()
  },
  
  get blogGrid() {
    return getRandomSquareAds(4)
  }
}

